import React, { useState } from "react";

import TuneIcon from "@mui/icons-material/Tune";
import {
  Autocomplete,
  Button,
  Divider,
  FormControlLabel,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useDateFormatSelect } from "store";
import { CurrencyType, SHEET_DATE_TYPES, SheetSettings } from "store/types";
import toIsoMidday from "utils/to-iso-midday";

import { currenciesArray } from "../../../../constants";
import { useSmartSheet } from "../../context/SmartSheet-context";

type DefaultDraft = Omit<SheetSettings, "exchangeDate">;

const DEFAULT_DRAFT: DefaultDraft = {
  use_accounting_date: false
};

interface Props {
  container?: Element | null;
}

export const SmartSheetDataSettings: React.FC<Props> = ({ container }) => {
  const dateFormat = useDateFormatSelect();
  const { sheetSettings, updateSheetSettings, hasNonDefaultSettings } = useSmartSheet();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [draft, setDraft] = useState(DEFAULT_DRAFT);
  const [datePickerOpen, setDatePickerOpen] = useState(false);

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setDraft({
      dateRange: sheetSettings.dateRange,
      use_accounting_date: sheetSettings.use_accounting_date ?? false,
      currency: sheetSettings.currency
    });
  };

  const handleCancel = () => {
    setAnchorEl(null);
  };

  const handleApply = () => {
    setAnchorEl(null);

    if (
      draft.dateRange === sheetSettings.dateRange &&
      draft.currency === sheetSettings.currency &&
      draft.use_accounting_date === sheetSettings.use_accounting_date
    ) {
      return;
    }

    updateSheetSettings({
      ...sheetSettings,
      ...(draft.dateRange !== undefined && { dateRange: draft.dateRange }),
      ...(draft.use_accounting_date !== undefined && {
        use_accounting_date: draft.use_accounting_date
      }),
      ...(draft.currency !== undefined && { currency: draft.currency })
    });
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date && date.isValid()) {
      setDraft((prev) => ({
        ...prev,
        dateRange: { to: toIsoMidday(date.toISOString()) }
      }));
    } else {
      setDraft((prev) => ({
        ...prev,
        dateRange: undefined
      }));
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<TuneIcon />}
        onClick={handleOpen}
        size="sm"
        sx={{
          textTransform: "none",
          color: hasNonDefaultSettings ? "#FFF" : "#071F35",
          borderColor: hasNonDefaultSettings ? "#071F35" : "#E6E9EB",
          bgcolor: hasNonDefaultSettings ? "#071F35" : "#E6E9EB",
          whiteSpace: "nowrap",
          "&:hover": {
            borderColor: "#0A3142",
            bgcolor: hasNonDefaultSettings ? "#0A3142" : undefined
          }
        }}
      >
        Sheet Settings
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        container={container ?? document.body}
        slotProps={{
          paper: {
            sx: {
              p: "24px",
              borderRadius: "12px",
              minWidth: 320,
              display: "flex",
              flexDirection: "column",
              gap: "14px"
            }
          }
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: "14px" }}>
          Sheet Settings
        </Typography>

        <DesktopDatePicker
          label="To Date"
          format={dateFormat}
          views={["year", "month", "day"]}
          open={datePickerOpen}
          onOpen={() => setDatePickerOpen(true)}
          onClose={() => setDatePickerOpen(false)}
          value={draft.dateRange ? dayjs(draft.dateRange.to) : null}
          onChange={handleDateChange}
          slotProps={{
            textField: {
              size: "small",
              fullWidth: true,
              error: false,
              InputLabelProps: { shrink: true },
              onClick: () => setDatePickerOpen(true)
            },
            popper: {
              container: container ?? document.body
            }
          }}
        />

        <Divider sx={{ borderColor: "#DBE8F5" }} />

        <RadioGroup
          row
          value={draft.use_accounting_date ? SHEET_DATE_TYPES.ACCOUNTING : SHEET_DATE_TYPES.EFFECTIVE}
          onChange={(_, value) =>
            setDraft((prev) => ({ ...prev, use_accounting_date: value === SHEET_DATE_TYPES.ACCOUNTING }))
          }
          sx={{ gap: 2 }}
        >
          <FormControlLabel
            value={SHEET_DATE_TYPES.ACCOUNTING}
            control={<Radio size="small" sx={{ p: 0 }} />}
            label="Accounting date"
            sx={{ m: 0, gap: "10px", "& .MuiTypography-root": { lineHeight: "20px" } }}
          />
          <FormControlLabel
            value={SHEET_DATE_TYPES.EFFECTIVE}
            control={<Radio size="small" sx={{ p: 0 }} />}
            label="Effective date"
            sx={{ m: 0, gap: "10px", "& .MuiTypography-root": { lineHeight: "20px" } }}
          />
        </RadioGroup>

        <Divider sx={{ borderColor: "#DBE8F5" }} />

        <Autocomplete<CurrencyType, false, true>
          options={currenciesArray}
          value={draft.currency ?? undefined}
          disableClearable
          sx={{ mb: "6px" }}
          onChange={(_, value) => setDraft((prev) => ({ ...prev, currency: value }))}
          slotProps={{
            popper: {
              container: container ?? document.body
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label="Currency" size="small" InputLabelProps={{ shrink: true }} />
          )}
        />

        <Stack direction="row" justifyContent="flex-end" gap={1}>
          <Button variant="outlined" onClick={handleCancel} sx={{ borderRadius: "20px", textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            sx={{ borderRadius: "20px", textTransform: "none", bgcolor: "#071F35" }}
          >
            Apply
          </Button>
        </Stack>
      </Popover>
    </>
  );
};
